import React from "react";
import { Link } from "react-router-dom";
import { useConsent } from "../contexts/ConsentContext";
import { useLanguage } from "../contexts/LanguageContext";
import { categoryLabels, technologyRegistry } from "../data/privacy";

const copy = {
  it: {
    eyebrow: "Cookie e tecnologie analoghe",
    title: "Cookie Policy",
    updated: "Ultimo aggiornamento: 9 settembre 2026 · Versione 2026-09-09",
    intro:
      "Questa pagina descrive le tecnologie memorizzate o consultate dal sito. Comprende cookie, localStorage e sessionStorage: funzioni tecniche diverse che possono comunque salvare informazioni nel browser.",
    controllerTitle: "1. Chi gestisce il sito",
    controller:
      "Il titolare è Eva Tazzari. Per domande puoi scrivere a info@evatazzari.com.",
    operationTitle: "2. Come funziona la scelta",
    operation:
      "Le tecnologie necessarie operano per fornire le funzioni richieste, proteggere i moduli e ricordare le preferenze. Google Analytics e PostHog non vengono caricati finché non autorizzi la relativa categoria. Le due categorie sono indipendenti, inizialmente disattivate e possono essere rifiutate senza perdere l’accesso al sito.",
    categoriesTitle: "3. Categorie",
    categories: {
      necessary: "Funzioni richieste per lingua, introduzione, sicurezza dei moduli e memorizzazione della scelta. Sono sempre attive e non vengono usate per misurare o profilare la navigazione.",
      audienceMeasurement: "Google Analytics misura visite e pagine consultate. Google può ricevere dati tecnici sul dispositivo e sulla navigazione. È attivo solo con consenso.",
      experienceAnalytics: "PostHog registra manualmente le visualizzazioni di pagina per produrre statistiche pseudonime. La cattura automatica delle interazioni e la registrazione delle sessioni sono disattivate. È attivo solo con consenso.",
    },
    inventoryTitle: "4. Inventario delle tecnologie",
    inventoryIntro:
      "La durata indica il limite configurato dal sito. Per le tecnologie gestite direttamente da terzi può variare secondo il servizio e si applica quanto indicato nell’informativa del fornitore.",
    table: {
      technology: "Tecnologia",
      provider: "Fornitore",
      category: "Categoria",
      storage: "Tipo di memoria",
      purpose: "Finalità",
      duration: "Durata",
    },
    thirdPartiesTitle: "5. Fornitori e trasferimenti",
    thirdParties:
      "Cloudflare riceve segnali tecnici quando la verifica Turnstile viene mostrata nei moduli. Con il consenso pertinente, Google e PostHog ricevono dati tecnici e di navigazione. Questi fornitori possono operare anche fuori dallo Spazio economico europeo. Servizio e configurazione determinano localizzazione e garanzie applicabili; puoi chiederne i dettagli al contatto privacy e consultare le informative collegate in fondo alla pagina.",
    controlTitle: "6. Modificare o revocare il consenso",
    control:
      "Puoi riaprire le impostazioni in qualsiasi momento e cambiare ciascuna categoria. La nuova scelta sostituisce la precedente; quando revochi una categoria, il sito interrompe la relativa raccolta e rimuove dal browser le chiavi note di Google Analytics e PostHog. La revoca non pregiudica la liceità del trattamento effettuato prima della revoca. Puoi anche cancellare cookie e dati dei siti dalle impostazioni del browser.",
    settings: "Riapri le impostazioni cookie",
    privacyLink: "Consulta la Privacy Policy",
    changesTitle: "7. Aggiornamenti",
    changes:
      "L’inventario viene aggiornato quando cambiano tecnologie, finalità o fornitori. Una modifica sostanziale comporta una nuova richiesta di scelta. La scelta corrente viene ricordata per 6 mesi, salvo cancellazione dei dati del browser o aggiornamento anticipato della policy.",
    externalTitle: "Informative dei fornitori",
    providerLinks: {
      google: "Google: privacy e dati",
      posthog: "PostHog: privacy",
      cloudflare: "Cloudflare: privacy",
    },
  },
  en: {
    eyebrow: "Cookies and similar technologies",
    title: "Cookie Policy",
    updated: "Last updated: 9 September 2026 · Version 2026-09-09",
    intro:
      "This page describes the technologies stored or accessed by the website. It covers cookies, localStorage and sessionStorage: technically different features that can all store information in your browser.",
    controllerTitle: "1. Who runs the website",
    controller:
      "The controller is Eva Tazzari. For questions, email info@evatazzari.com.",
    operationTitle: "2. How your choice works",
    operation:
      "Necessary technologies operate to provide requested features, protect forms and remember preferences. Google Analytics and PostHog are not loaded until you authorise the relevant category. The two categories are independent, initially switched off and may be rejected without losing access to the website.",
    categoriesTitle: "3. Categories",
    categories: {
      necessary: "Features requested for language, the introduction, form security and remembering your choice. They are always active and are not used to measure or profile your browsing.",
      audienceMeasurement: "Google Analytics measures visits and pages viewed. Google may receive technical data about the device and browsing. It is active only with consent.",
      experienceAnalytics: "PostHog manually records page views to produce pseudonymous statistics. Automatic interaction capture and session recording are disabled. It is active only with consent.",
    },
    inventoryTitle: "4. Technology inventory",
    inventoryIntro:
      "The lifetime shown is the limit configured by the website. For technologies managed directly by third parties, it may vary with the service and the provider’s notice applies.",
    table: {
      technology: "Technology",
      provider: "Provider",
      category: "Category",
      storage: "Storage type",
      purpose: "Purpose",
      duration: "Lifetime",
    },
    thirdPartiesTitle: "5. Providers and transfers",
    thirdParties:
      "Cloudflare receives technical signals when the Turnstile check is shown in forms. With the relevant consent, Google and PostHog receive technical and browsing data. These providers may also operate outside the European Economic Area. The service and configuration determine the processing location and applicable safeguards; you may request details from the privacy contact and consult the notices linked at the end of this page.",
    controlTitle: "6. Changing or withdrawing consent",
    control:
      "You can reopen settings at any time and change each category. Your new choice replaces the previous one; when you withdraw a category, the website stops the related collection and removes known Google Analytics and PostHog keys from the browser. Withdrawal does not affect the lawfulness of processing carried out before it. You can also erase cookies and site data through your browser settings.",
    settings: "Reopen cookie settings",
    privacyLink: "Read the Privacy Policy",
    changesTitle: "7. Updates",
    changes:
      "The inventory is updated when technologies, purposes or providers change. A material change triggers a new request for your choice. The current choice is remembered for 6 months unless you clear browser data or the policy is updated sooner.",
    externalTitle: "Providers’ privacy notices",
    providerLinks: {
      google: "Google: privacy and data",
      posthog: "PostHog: privacy",
      cloudflare: "Cloudflare: privacy",
    },
  },
};

function localise(value, language) {
  if (value && typeof value === "object") return value[language] || value.it || value.en || "";
  return value;
}

export default function CookiePolicy() {
  const { language } = useLanguage();
  const { openPreferences } = useConsent();
  const content = copy[language] || copy.it;

  return (
    <article className="legal-page">
      <header className="legal-page-header">
        <p className="small-text">{content.eyebrow}</p>
        <h1>{content.title}</h1>
        <p className="small-text">{content.updated}</p>
        <p className="legal-page-lead">{content.intro}</p>
      </header>

      <div>
        <section className="legal-section">
          <h2>{content.controllerTitle}</h2>
          <p>{content.controller}</p>
        </section>

        <section className="legal-section">
          <h2>{content.operationTitle}</h2>
          <p>{content.operation}</p>
        </section>

        <section className="legal-section">
          <h2>{content.categoriesTitle}</h2>
          {Object.keys(categoryLabels).map((category) => (
            <div key={category}>
              <h3>{localise(categoryLabels[category], language)}</h3>
              <p>{content.categories[category]}</p>
            </div>
          ))}
        </section>

        <section className="legal-section">
          <h2>{content.inventoryTitle}</h2>
          <p>{content.inventoryIntro}</p>
          <div className="legal-table-wrap">
            <table className="legal-table">
              <thead>
                <tr>
                  <th scope="col">{content.table.technology}</th>
                  <th scope="col">{content.table.provider}</th>
                  <th scope="col">{content.table.category}</th>
                  <th scope="col">{content.table.storage}</th>
                  <th scope="col">{content.table.purpose}</th>
                  <th scope="col">{content.table.duration}</th>
                </tr>
              </thead>
              <tbody>
                {technologyRegistry.map((technology) => (
                  <tr key={technology.name}>
                    <th scope="row">{technology.name}</th>
                    <td>{technology.provider}</td>
                    <td>{localise(categoryLabels[technology.category], language)}</td>
                    <td>{localise(technology.storage, language)}</td>
                    <td>{localise(technology.purpose, language)}</td>
                    <td>{localise(technology.duration, language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="legal-section">
          <h2>{content.thirdPartiesTitle}</h2>
          <p>{content.thirdParties}</p>
        </section>

        <section className="legal-section">
          <h2>{content.controlTitle}</h2>
          <p>{content.control}</p>
          <div className="legal-actions">
            <button type="button" onClick={openPreferences}>
              {content.settings}
            </button>
            <Link to={`/${language}/privacy`}>
              {content.privacyLink}
            </Link>
          </div>
        </section>

        <section className="legal-section">
          <h2>{content.changesTitle}</h2>
          <p>{content.changes}</p>
        </section>

        <section className="legal-section">
          <h2>{content.externalTitle}</h2>
          <ul>
            <li>
              <a className="legal-inline-link" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {content.providerLinks.google}
              </a>
            </li>
            <li>
              <a className="legal-inline-link" href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">
                {content.providerLinks.posthog}
              </a>
            </li>
            <li>
              <a className="legal-inline-link" href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">
                {content.providerLinks.cloudflare}
              </a>
            </li>
          </ul>
        </section>
      </div>
    </article>
  );
}
